/**
 * TopBar Component
 *
 * Application header/top bar with authentication, language switching,
 * audio notification controls, and jackpot counter.
 * Extracted from App.tsx for better separation of concerns.
 */

import { memo } from 'react';
import { Volume2, VolumeX, LogIn, FileText } from 'lucide-react';
import { UserMenu } from '../auth';
import { TokenBalance } from '../tokens';
import { JackpotCounter } from '../jackpot';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../hooks/useAuth';
import { useTokenBalance } from '../../hooks/useTokenBalance';

interface TopBarProps {
  audioEnabled: boolean;
  onToggleAudio: () => void;
  onLoginClick: () => void;
  onBuyTokensClick: () => void;
  onJackpotClick?: () => void;
}

export const TopBar = memo(function TopBar({
  audioEnabled,
  onToggleAudio,
  onLoginClick,
  onBuyTokensClick,
  onJackpotClick,
}: TopBarProps) {
  const { user, loading: authLoading } = useAuth();
  const {
    balance: tokenBalance,
    fullBalance: tokenFullBalance,
    loading: tokenBalanceLoading,
  } = useTokenBalance();
  const { t } = useTranslation();

  return (
    <header className="app-topbar">
      {/* Left section: User + Tokens + Buy */}
      <div className="flex-1 flex items-center space-x-2 md:space-x-4 flex-shrink-0 min-w-0">
        {/* Auth: Login Button or User Menu (far left) */}
        {!authLoading &&
          (user ? (
            <UserMenu />
          ) : (
            <button
              onClick={onLoginClick}
              className="flex items-center gap-2 px-3 py-2 rounded-lg
                         bg-gradient-to-r from-primary-600 to-primary-500
                         hover:from-primary-500 hover:to-primary-400
                         text-white font-medium text-sm
                         transition-all duration-200 shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40"
              data-testid="login-button"
            >
              <LogIn className="h-4 w-4" />
              <span className="hidden sm:inline">{t('auth.signIn')}</span>
            </button>
          ))}

        {/* Token Balance + Buy (next to user) */}
        {user && (
          <TokenBalance
            balance={tokenBalance}
            fullBalance={tokenFullBalance}
            isLoading={tokenBalanceLoading}
            onBuyClick={onBuyTokensClick}
          />
        )}
      </div>

      {/* Center: Jackpot Counter */}
      <JackpotCounter onClick={onJackpotClick} />

      {/* Right section: Whitepaper + Languages + Audio */}
      <div className="flex-1 flex items-center justify-end space-x-2 md:space-x-4">
        {/* Whitepaper Link (next to languages) */}
        <a
          href="/whitepaper"
          className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg
                     text-white/60
                     hover:bg-white/10
                     hover:text-primary-400
                     transition-colors text-sm font-medium"
          title={t('whitepaper.title')}
        >
          <FileText className="h-4 w-4" />
          <span>Whitepaper</span>
        </a>

        {/* Audio Toggle Button */}
        <button
          onClick={onToggleAudio}
          className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          title={
            audioEnabled ? t('app.header.audioTooltipDisable') : t('app.header.audioTooltipEnable')
          }
          data-testid="audio-toggle-button"
        >
          {audioEnabled ? (
            <Volume2 className="h-5 w-5 text-white/70" />
          ) : (
            <VolumeX className="h-5 w-5 text-white/40" />
          )}
        </button>
      </div>
    </header>
  );
});
